// Period calculation utilities
import type { DateRange, PeriodOption } from '@/types/report';

export function getToday(): DateRange {
  const now = new Date();
  const start = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 0, 0, 0);
  const end = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 23, 59, 59);
  return { start, end };
}

export function getThisWeek(): DateRange {
  const now = new Date();
  const dayOfWeek = now.getDay();
  const diff = dayOfWeek === 0 ? 6 : dayOfWeek - 1; // Monday as first day
  
  const start = new Date(now);
  start.setDate(now.getDate() - diff);
  start.setHours(0, 0, 0, 0);
  
  const end = new Date(start);
  end.setDate(start.getDate() + 6);
  end.setHours(23, 59, 59, 999);
  
  return { start, end };
}

export function getThisMonth(): DateRange {
  const now = new Date();
  const start = new Date(now.getFullYear(), now.getMonth(), 1, 0, 0, 0);
  const end = new Date(now.getFullYear(), now.getMonth() + 1, 0, 23, 59, 59);
  return { start, end };
}

export function getLastMonth(): DateRange {
  const now = new Date();
  const start = new Date(now.getFullYear(), now.getMonth() - 1, 1, 0, 0, 0);
  const end = new Date(now.getFullYear(), now.getMonth(), 0, 23, 59, 59);
  return { start, end };
}

export function getThisQuarter(): DateRange {
  const now = new Date();
  const quarter = Math.floor(now.getMonth() / 3);
  const start = new Date(now.getFullYear(), quarter * 3, 1, 0, 0, 0);
  const end = new Date(now.getFullYear(), (quarter + 1) * 3, 0, 23, 59, 59);
  return { start, end };
}

export function getThisYear(): DateRange {
  const now = new Date();
  const start = new Date(now.getFullYear(), 0, 1, 0, 0, 0);
  const end = new Date(now.getFullYear(), 11, 31, 23, 59, 59);
  return { start, end };
}

export function getLast7Days(): DateRange {
  const end = new Date();
  end.setHours(23, 59, 59, 999);
  
  const start = new Date();
  start.setDate(end.getDate() - 6);
  start.setHours(0, 0, 0, 0);
  
  return { start, end };
}

export function getLast30Days(): DateRange {
  const end = new Date();
  end.setHours(23, 59, 59, 999);
  
  const start = new Date();
  start.setDate(end.getDate() - 29);
  start.setHours(0, 0, 0, 0);
  
  return { start, end };
}

export function getLast90Days(): DateRange {
  const end = new Date();
  end.setHours(23, 59, 59, 999);
  
  const start = new Date();
  start.setDate(end.getDate() - 89);
  start.setHours(0, 0, 0, 0);
  
  return { start, end };
}

export function getCustomRange(start: Date, end: Date): DateRange {
  const startDate = new Date(start);
  startDate.setHours(0, 0, 0, 0);
  
  const endDate = new Date(end);
  endDate.setHours(23, 59, 59, 999);
  
  return { start: startDate, end: endDate };
}

export const PERIOD_OPTIONS: PeriodOption[] = [
  {
    label: 'Aujourd\'hui',
    labelAr: 'اليوم',
    value: 'day',
    getDates: getToday,
  },
  {
    label: 'Cette semaine',
    labelAr: 'هذا الأسبوع',
    value: 'week',
    getDates: getThisWeek,
  },
  {
    label: 'Ce mois',
    labelAr: 'هذا الشهر',
    value: 'month',
    getDates: getThisMonth,
  },
  {
    label: 'Ce trimestre',
    labelAr: 'هذا الفصل',
    value: 'quarter',
    getDates: getThisQuarter,
  },
  {
    label: 'Cette année',
    labelAr: 'هذه السنة',
    value: 'year',
    getDates: getThisYear,
  },
];

export function formatDate(date: Date, format: 'short' | 'long' = 'short'): string {
  if (format === 'short') {
    return date.toLocaleDateString('fr-DZ', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    });
  }
  
  return date.toLocaleDateString('fr-DZ', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });
}

export function formatMonth(date: Date): string {
  return date.toLocaleDateString('fr-DZ', {
    month: 'long',
    year: 'numeric',
  });
}

export function getPreviousPeriod(range: DateRange): DateRange {
  const duration = range.end.getTime() - range.start.getTime();
  
  const end = new Date(range.start.getTime() - 1);
  const start = new Date(end.getTime() - duration);
  
  return { start, end };
}

export function getDaysDiff(start: Date, end: Date): number {
  const diffTime = Math.abs(end.getTime() - start.getTime());
  return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
}
